import { jsx } from '@emotion/react/jsx-runtime';
import { createPolymorphicComponent, useSlot, useFocus, validityColorMap, DisabledEffect, FocusRing, SlotProvider } from '@nettbureau/core';
import { useFieldContext } from '@nettbureau/field';
import { Rectangle } from '@nettbureau/shape';
import { useCss } from '@nettbureau/styling';
import { useCallback } from 'react';
import { useObjectRef } from 'react-aria';

/**
 * Ordered list of available base field sizes.
 */ const baseFieldSizes = [
    'xs',
    'sm',
    'md',
    'lg',
    'xl'
];
/**
 * Text fields let users enter and edit text.
 */ const BaseField = createPolymorphicComponent((props, ref)=>{
    const fieldProps = useFieldContext();
    const [mergedProps, mergedRef] = useSlot('BaseField', {
        ...fieldProps,
        ...props
    }, ref);
    const { autoFocus, children, css, disabled, name, onBlur, onFocus, onFocusChange, required, size = 'md', validity, variant = 'base', ...otherProps } = mergedProps;
    const inputRef = useObjectRef();
    const [{ focused, focusVisible }, { focusProps }] = useFocus({
        autoFocus,
        disabled,
        isTextInput: true,
        onBlur,
        onFocus,
        onFocusChange
    });
    const validityColor = validity && validityColorMap[validity];
    const outline = validityColor ?? (focused ? 'accent' : 'neutral');
    const outlineTone = outline === 'neutral' ? 'medium' : 'strong';
    const outlineWidth = outline === 'neutral' ? 'regular' : 'thick';
    const handleMouseDown = useCallback((event)=>{
        if (event.target !== inputRef.current) {
            event.preventDefault();
            inputRef.current?.focus();
        }
    }, []);
    const fieldCss = useCss(!disabled && {
        cursor: 'text'
    });
    const overrides = {
        BaseInput: {
            ...focusProps,
            ref: inputRef,
            autoFocus,
            disabled,
            name,
            required
        }
    };
    return /*#__PURE__*/ jsx(DisabledEffect, {
        disabled: disabled,
        children: /*#__PURE__*/ jsx(FocusRing, {
            color: outline,
            focused: focusVisible,
            children: /*#__PURE__*/ jsx(Rectangle, {
                "data-text-field": true,
                ...otherProps,
                ref: mergedRef,
                css: [
                    fieldCss,
                    css
                ],
                minHeight: size,
                fill: variant === 'base' ? 'base' : 'neutral',
                fillTone: variant === 'base' ? 'medium' : 'soft',
                onMouseDown: handleMouseDown,
                outline: outline,
                outlineTone: outlineTone,
                outlineWidth: variant === 'base' || outline !== 'neutral' ? outlineWidth : 'none',
                shadow: variant === 'base' ? 'weaker' : 'none',
                children: /*#__PURE__*/ jsx(SlotProvider, {
                    value: overrides,
                    children: children
                })
            })
        })
    });
});

export { BaseField, baseFieldSizes };
//# sourceMappingURL=base-field.js.map
